import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import { Group } from 'APP/store/Groups/Group';
import { ChatMessage } from 'APP/store/Messages/Message/ChatMessage/ChatMessage';
import { Message } from 'APP/store/Messages/Message/Message';
import { Me } from 'STORE/Users/User/Me/Me';

interface IGroupDescriptionPresenter {
  lastMessage: ChatMessage | Message | null;
  sender: string | null;
  isShowDraft: boolean;
  draftText: string;
  payload: ChatMessage['payload'] | Message['payload'] | null;
  group: Group;
  setGroup(group: Group): void;
}

export const useGroupDescriptionPresenter = (group: Group): IGroupDescriptionPresenter => {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IGroupDescriptionPresenter>(() => ({
    group,

    get lastMessage(): ChatMessage | Message | null {
      return presenter.group.lastMessage;
    },

    get payload(): ChatMessage['payload'] | Message['payload'] | null {
      if (presenter.lastMessage instanceof ChatMessage) {
        return presenter.lastMessage.renderPayload;
      }

      return presenter.lastMessage?.payload || null;
    },

    get sender(): string | null {
      if (!presenter.lastMessage || !presenter.group.isChatGroup) {
        return null;
      }

      return presenter.lastMessage.senderUser instanceof Me
        ? t('chat_details_myself')
        : presenter.lastMessage.lastMessageSenderName.split(' ')[0];
    },
    get isShowDraft(): boolean {
      return presenter.group.isShowDraft;
    },

    get draftText(): string {
      return presenter.group.draftInput?.text || '';
    },

    setGroup(group: Group): void {
      presenter.group = group;
    },
  }));

  useEffect(() => {
    presenter.setGroup(group);
  }, [group]);

  return presenter;
};
